import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledOcticon } from '@primer/react';
import { BugIcon, DownloadIcon } from '@primer/octicons-react';

import { CSVLink } from 'react-csv';

import { percentFormatter } from '../../../../utils/formatters';
import { RELATIVE_TIME_RANGES } from '../../../../constants/time.constants';

import InfrastructureConsumptionGrid from '../InfrastructureConsumptionCard/InfrastructureConsumptionGrid';
import LargeTimeRangePicker from '../../../TimeRangePicker/LargeTimeRangePicker';

import PipelineStatsBreakdownTable from '../../pipelines/PipelineStatsBreakdownTable/PipelineStatsBreakdownTable';
import PaginationRow from '../../../common/PaginationRow/PaginationRow';
import { StaticTimeRangeProvider, useStaticTimeRange } from '../../../contexts/TimeRangeContext/StaticTimeRangeContext';
import Button from '../../../common/Button/Button';
import Icon from '../../../common/icons/Icon';
import CardHeader from '../../../common/Labels/CardHeader'
import PipelineTableFilterModal from '../../pipelines/PipelineTableFilterModal/PipelineTableFilterModal';
import useWorkspaceContext from '../../../workspaces/WorkspaceContext/WorkspaceContext';
import usePipelineListWithAggregatedMetrics
  from '../../../pipeline_metrics/query_utils/usePipelineListWithAggregatedMetrics';
import PipelineIcon from '../../../common/icons/PipelineIcon';
import CardView from '../../../common/CardView/CardView';
import ErrorSlate from '../../../ErrorSlate/ErrorSlate';
import FilterCountButton from '../../FilterCountButton';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import { formatMemoryUnitsAsGB, formatMemoryUnitsAsGi } from '../../../../utils/prometheus_utils';
import fromUnixTime from 'date-fns/fromUnixTime';
import LoadingSpinner from '../../../common/LoadingSpinner/LoadingSpinner';
import StatusBanner from '../../../common/StatusBanner/StatusBanner';

const DEFAULT_FILTER_CONFIG = { workspaces: [] }

const generateCsvTitle = (title, startDate, endDate) => {
  const formattedStartDate = fromUnixTime(startDate).toISOString().split('.')[0];
  const formattedEndDate = fromUnixTime(endDate).toISOString().split('.')[0];
  return `${title.toLowerCase().replaceAll(' ', '_')}_${formattedStartDate}_to_${formattedEndDate}.csv`;
};

const convertToCSVData = (pipelinesWithMetrics) => {
  const HEADER_ROW = [
    'Name', 'Workspace', 'Status',
    'Avg Cpu (vCPUs Used)', 'Avg Cpu (vCPUs Req)', 'Avg Cpu (%)',
    'Avg Gpu (Used)', 'Avg Gpu (Req)', 'Avg Gpu (%)',
    'Avg Memory (Gi Used)', 'Avg Memory (Gi Req)', 'Avg Memory (%)',
    'Arch', 'Accel', 'Deployment Label', 'Log Size',
  ]
  const csvData = pipelinesWithMetrics.map(({ pipeline, deployment, workspace, metrics }) => {
    return [
      pipeline?.name || '--',
      workspace?.name || '--',
      deployment?.deployed ? 'Active' : 'Inactive',
      (isNumber(metrics.cpuUsed) && metrics?.cpuUsed >= 0) ? metrics.cpuUsed : '--',
      (isNumber(metrics.cpuReq) && metrics?.cpuReq >= 0) ? metrics.cpuReq : '--',
      (isNumber(metrics.cpuPercent) && metrics?.cpuPercent >= 0) ? percentFormatter.format(metrics.cpuPercent) : '--',
      (isNumber(metrics.gpuUsed) && metrics?.gpuUsed >= 0) ? metrics.gpuUsed : '--',
      (isNumber(metrics.gpuReq) && metrics?.gpuReq >= 0) ? metrics.gpuReq : '--',
      (isNumber(metrics.gpuPercent) && metrics?.gpuPercent >= 0) ? percentFormatter.format(metrics.gpuPercent) : '--',
      (isNumber(metrics.memUsed) && metrics?.memUsed >= 0) ? formatMemoryUnitsAsGi(metrics.memUsed) : '--',
      (isNumber(metrics.memReq) && metrics?.memReq >= 0) ? formatMemoryUnitsAsGi(metrics.memReq) : '--',
      (isNumber(metrics.memPercent) && metrics?.memPercent >= 0) ? percentFormatter.format(metrics.memPercent) : '--',
      deployment?.engine_config?.engine?.arch || '--',
      deployment?.engine_config?.engine?.accel || '--',
      isString(deployment?.engine_config?.engine?.node_selector) ? deployment?.engine_config?.engine?.node_selector : 'None',
      (isNumber(metrics.logSize) && metrics?.logSize >= 0) ? formatMemoryUnitsAsGB(metrics.logSize) : '--',
    ]
  });
  return [
    HEADER_ROW,
    ...csvData,
  ];
}

const BugGraphic = () => (
  <StyledOcticon
    className="mb-2 text-brand"
    icon={BugIcon}
    size={64}
  />
)

const NoPipelinesGraphic = () => (
  <StyledOcticon
    className="mb-2 text-brand"
    icon={PipelineIcon}
    size={16}
  />
)

const ExportButton = ({ csvData, startDate, endDate, disabled = false }) => {
  if (disabled) {
    return (
      <Button className="mr-2" variant="brand" disabled>
        <div className="flex items-center justify-center">
          <Icon
            icon={DownloadIcon}
            rectWidth="8"
            rectHeight="8"
            width="8"
            height="8"
          />
          <span className="ml-2">Export</span>
        </div>
      </Button>
    )
  }

  const fileName = generateCsvTitle('Infrastructure Usage Breakdown By Pipelines Over Time', startDate, endDate);
  return (
    <CSVLink
      data={csvData}
      filename={fileName}
      className="text-left text-brand mr-2 flex justify-center items-center"
    >
      <Button variant="brand">
        <div className="flex items-center justify-center">
          <Icon
            icon={DownloadIcon}
            rectWidth="8"
            rectHeight="8"
            width="8"
            height="8"
          />
          <span className="ml-2">Export</span>
        </div>
      </Button>
    </CSVLink>
  )
}

const getErrorOrEmptyMessage = (error, numPipelines, numFilteredPipelines) => {
  if (!error) {
    if (!numPipelines) {
      return 'No pipelines found.'
    }
    if (!numFilteredPipelines) {
      return 'No pipelines matching the selected filters were found.'
    }
  }

  return error;
}

const countActivePipelineFilters = (filterObj: any = {}) => {
  let filterCount = 0;
  if (filterObj.workspaces?.length) {
    filterCount += 1;
  }

  return filterCount;
}

const PipelineTable = ({
  pipelines,
  isLoading,
  error,
  startTimestamp,
  endTimestamp,
  workspaces = [],
}) => {
  const [selectedPage, setSelectedPage] = useState(0);
  const [resultsPerPage, setResultsPerPage] = useState(10);
  const [filter, setFilterObj] = useState({ workspaces: [] })
  const [showFilterDialog, setShowFilterDialog] = useState(false);

  const filteredPipelines = useMemo(() => {
    if (!pipelines) {
      return [];
    }
    return pipelines.filter((pipeline) => {
      // Filter out inactive pipelines
      if (!pipeline?.deployment?.deployed) {
        return false;
      }
      if (filter?.workspaces?.length) {
        const selectedIds: number[] = filter.workspaces.map(({ id }) => id);
        const pipelineWorkspaceId: number = pipeline?.workspace?.id;

        return selectedIds.includes(pipelineWorkspaceId);
      }
      return true;
    })
  }, [filter.workspaces, pipelines])


  const pipelineCsvData = useMemo(() => {
    return convertToCSVData(filteredPipelines);
  }, [filteredPipelines]);

  const onFilterButtonClick = useCallback(() => {
    setShowFilterDialog(true);
  }, [])

  const onFilterConfirm = useCallback((newFilter) => {
    setFilterObj(newFilter);
    setShowFilterDialog(false);
  }, [])

  const onClear = useCallback(() => {
    setFilterObj({ ... DEFAULT_FILTER_CONFIG });
    setShowFilterDialog(false)
  }, [])

  const onDialogToggle = (row) => {
    if (!row) {
      setShowFilterDialog(false);
    }
  }

  const errorText = getErrorOrEmptyMessage(error, pipelines?.length || 0, filteredPipelines?.length || 0)
  const errorProps = error ? {} : { CTAInfo: null }

  const numActiveFilters = countActivePipelineFilters(filter);

  return (
    // @ts-ignore
    <div>
      <div className="flex justify-start">
        <CardHeader className="">
          Infrastructure Usage Breakdown by Pipelines Over Time
        </CardHeader>
      </div>
      <div className="flex justify-end w-full relative mb-2">
        <ExportButton
          startDate={startTimestamp}
          endDate={endTimestamp}
          csvData={pipelineCsvData}
          disabled={isLoading}
        />
        <FilterCountButton onClick={onFilterButtonClick} activeFilters={numActiveFilters} disabled={isLoading} />
      </div>
      <div className="relative">
        {
          isLoading ? (
            <LoadingSpinner
              animationKey="model-list-spinner"
              size="medium"
              //@ts-ignore
              loadingText="Loading..."
              useOverlay
            />
          ) : null
        }
        {
          (!filteredPipelines?.length || error) ? (
            <CardView className="p-4" disableFullHeight>
              <ErrorSlate
                errorText={errorText}
                // @ts-ignore
                subText={null}
                Graphic={error ? BugGraphic : NoPipelinesGraphic}
                {...errorProps}
              />
            </CardView>
          ) : (
            <PipelineStatsBreakdownTable
              pipelines={filteredPipelines}
              selectedPage={selectedPage}
              resultsPerPage={resultsPerPage}
            />
          )
        }
        <PaginationRow
          className="p-4"
          numberOfResults={filteredPipelines?.length || 0}
          selectedPage={selectedPage}
          resultsPerPage={resultsPerPage}
          // @ts-ignore
          setSelectedPage={setSelectedPage}
          // @ts-ignore
          setResultsPerPage={setResultsPerPage}
        />
        {
          showFilterDialog && (
            <PipelineTableFilterModal
              workspaces={workspaces}
              // @ts-ignore
              filter={filter}
              showDialog={showFilterDialog}
              // @ts-ignore
              onDialogToggle={onDialogToggle}
              // @ts-ignore
              onFilterConfirm={onFilterConfirm}
              onClear={onClear}
              hideStatusDropdown
            />
          )
        }
      </div>
    </div>
  )
}

export const AdminMetricsTab = () => {
  const [showError, setShowError] = useState(false);
  const {
    startTimestamp,
    endTimestamp,
    selectedTimeRange,
    onTimeRangeChange,
  }: any = useStaticTimeRange()

  const { workspaces } = useWorkspaceContext();

  const {
    pipelinesWithMetrics: pipelines,
    isLoading,
    error,
  } = usePipelineListWithAggregatedMetrics({
    startTimestamp, endTimestamp
  });

  useEffect(() => {
    if (error) {
      setShowError(true);
    } else {
      setShowError(false);
    }
  }, [error])

  return (
    <div className="w-full">
      {
        showError && (
          // @ts-ignore
          <StatusBanner
            className="mb-2"
            status="warning"
            title="Warning"
            message={error}
            onClose={() => {setShowError(false)}}
          />
        )
      }
      <div className="flex flex-row justify-end w-full">
        <LargeTimeRangePicker
          defaultRange={RELATIVE_TIME_RANGES[9] as any}
          selectedRange={selectedTimeRange}
          onChange={onTimeRangeChange}
        />
      </div>
      <InfrastructureConsumptionGrid
        startTimestamp={startTimestamp}
        endTimestamp={endTimestamp}
      />
      <PipelineTable
        pipelines={pipelines}
        isLoading={isLoading}
        error={error}
        startTimestamp={startTimestamp}
        endTimestamp={endTimestamp}
        workspaces={workspaces}
      />
    </div>
  )
}

export const ContextualAdminMetricsTab = () => {
  return (
    <div>
      <StaticTimeRangeProvider>
        <AdminMetricsTab />
      </StaticTimeRangeProvider>
    </div>
  )
}

export default ContextualAdminMetricsTab;
